/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import { getLogger } from '../../utils/SearchLogger';
import Request from '../../utils/Request';
import getConfig from '../../config';
import uuid from 'uuid/v4';

export const fetchUserPreferences = async ({
	searchHostContext,
}: {
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const hostname = getConfig(searchHostContext.environment, 'SEARCH_SERVER');
		const path = `/v1/users/me/preference?surface=${searchHostContext.hostInfo.surface}&client=${searchHostContext.hostInfo.client}`;
		const curRequestId = uuid();
		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			authorization: `Bearer ${searchHostContext.imsInfo.accessToken}`,
			'x-request-id': curRequestId,
		};
		const userPreference = await Request.withHeaders(headers).send('GET', `https://${hostname}${path}`);
		// Handling error cases by sending true in genAIOptIn workflow.
		if (userPreference.isError()) {
			return { genAIOptIn: true };
		}
		const payload: any = userPreference.payload();
		return payload?.userpreference ?? { genAIOptIn: true };
	} catch (e) {
		return { genAIOptIn: true };
	}
};

export const storeUserPreferences = async ({
	genAiPreference,
	searchHostContext,
}: {
	genAiPreference: boolean;
	searchHostContext: HostContext;
}): Promise<unknown> => {
	try {
		const hostname = getConfig(searchHostContext.environment, 'SEARCH_SERVER');
		const path = '/v1/users/me/preference';
		const curRequestId = uuid();
		const startTime = Date.now();
		const surface = searchHostContext.hostInfo.surface;
		const client = searchHostContext.hostInfo.client;

		const headers = {
			'Content-Type': 'application/json',
			'x-api-key': searchHostContext.hostInfo.apiKey,
			authorization: `Bearer ${searchHostContext.imsInfo.accessToken}`,
			'x-request-id': curRequestId,
		};

		const bodyData = {
			surface,
			client,
			genAIOptIn: genAiPreference,
		};
		const putData = JSON.stringify(bodyData);

		const preferenceResponse = await Request.withPayload(putData)
			.withHeaders(headers)
			.send('PUT', `https://${hostname}${path}`);

		/*
		if (preferenceResponse.isError()) {
			const loggerData = {
				url: `https://${hostname}${path}`,
				status: preferenceResponse.status(),
				message: preferenceResponse.statusText(),
				requestId: curRequestId,
				duration: Date.now() - startTime,
				performanceTiming: PerformanceUtils.getPerformanceTimingByName(historyResponse.requestUrl()),
			};
			getLogger().error('Search Service', 'Problem with Search History put request', loggerData);
			const searchServiceError = new Error('Push history error');
			throw searchServiceError;
		}
		*/
		return preferenceResponse.data();
	} catch (e) {
		getLogger().error('Autocomplete Service', 'Autocomplete Service connect error', e.toString());
		const preferenceError = new Error('Preference Service connect error');
		return preferenceError;
	}
};
